import {
  Card,
  CardContent,
  CircularProgress,
  Grid,
  RACCard,
  RACModal,
  RACButton,
  Typography,
  RACTextbox,
  RACSearchDropdown,
  Divider,
} from '@rentacenter/racstrap';

import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  getClassifications,
  getPricing,
  getPricingClassifications,
} from '../../../api/user';
import {
  ComboBoxData,
  PricingData,
  ShopLocalProductUI,
} from '../../../types/types';
import { emptyField } from '../../../constants/constants';
import { localSourcingStyles } from '../../../JSstyles/localSourcingStyles';

import {
  ShopLocalProductInfo,
  ShopLocalRmsInfo,
  useShopLocalActions,
  useShopLocalData,
} from './ShopLocalProvider';
import {
  useNationalPartnerActions,
  useNationalPartnerData,
} from '../NationalPartnerProvider';
import { validatePrefundSLA } from '../validatePrefundSLA';
import SecondFactor from '../../Rental/TwoFactorPopup/TwoFactorPopup';
import {
  removeAcimaItems,
  updateResponseToHaveStrings,
} from '../../../utils/utils';

export interface ModalProps {
  editMode: boolean;
  onClose: (onclose: boolean) => void;
  onProcess: (onclose: boolean) => void;
}
const emptyProductListUI: ShopLocalProductUI = {
  selectedDepartment: emptyField,
  selectedSubDepartment: emptyField,
  selectedBraket: emptyField,
  productNameInput: '',
  quantity: '',
  quantityUI: '',
  unitPriceInputUI: '',
  unitPriceInput: '',
  typeSelected: '',
  subTypeSelected: '',
  rmsItemNumber: '',
  productPricing: {
    weeklyRate: '',
    weeklyTerm: '',
    priceWithFudgeFactor: '',
    rmsItemNumber: '',
    errMsg: '',
    cashPrice: '',
    totalCost: '',
  },
  departmentOptions: [],
  bracketOptions: [],
  subdepartmentOptions: [],
};

export const ShopLocalProductModal = ({
  onClose,
  onProcess,
  editMode,
}: ModalProps) => {
  const classes = localSourcingStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [progressToDeterminePricing, setProgressToDeterminePricing] =
    useState<boolean>(false);
  const lookUpPricing = useRef<boolean>(true);
  const [errmsg, setErrMsg] = useState<string>();
  const [continueEnabled, setContinueEnabled] = useState<boolean>(false);
  const [addProductEnabled, setAddProductEnabled] = useState<boolean>(true);
  const [removeProductEnabled, setRemoveProductEnabled] =
    useState<boolean>(true);
  const [classifications, setClassifications] = useState<any>(undefined);

  const [departments, setDepartments] = useState<ComboBoxData[]>([emptyField]);

  const [productListUI, setProductListUI] = useState<ShopLocalProductUI[]>([
    emptyProductListUI,
  ]);

  const [localProductsUI, setLocalProductsUI] = useState<ShopLocalProductUI[]>([
    emptyProductListUI,
  ]);

  const { calcTotalPricesWithFudgeFactor } = useNationalPartnerActions();
  const { enablePricingProductClassifications } = useNationalPartnerData();

  const { vendorName, estimatedWeeklyPayment, totalCost, products } =
    useShopLocalData();

  const getComboItem = (value: string, comboBox: ComboBoxData[]) => {
    return comboBox.find((one) => one.value == value) || emptyField;
  };

  const { storePrefundMaxAmount, dmPrefundMaxAmount } =
    useNationalPartnerData();
  const [showSLA, setShowSLA] = useState(false);
  const DEFAULT_ROLE = 'RD';
  const [slaCompleted, setSlaCompleted] = useState(false);

  const updateDisplayToPreviouslyUsed = () => {
    if (editMode && products) {
      if (products.length == 0) {
        // nothing was saved so create the default
        setProductListUI([emptyProductListUI]);
        setLocalProductsUI([emptyProductListUI]);
      } else {
        // create the UI list and set the values previously chosen
        const resetProductList: ShopLocalProductUI[] = [];

        products.map((oneProduct) => {
          const rmsInfo: ShopLocalRmsInfo = oneProduct.rmsInfo;

          const subList = chooseSubDepartments(rmsInfo.selectedDepartment);

          const brakList = chooseBrackets(
            rmsInfo.selectedDepartment,
            rmsInfo.selectedSubDepartment
          );

          const one: ShopLocalProductUI = {
            selectedDepartment: getComboItem(
              rmsInfo.selectedDepartment,
              departments
            ),
            selectedSubDepartment: getComboItem(
              rmsInfo.selectedSubDepartment,
              subList
            ),
            departmentOptions: departments,
            subdepartmentOptions: subList,
            bracketOptions: brakList,
            selectedBraket: getComboItem(rmsInfo.selectedBraket, brakList),
            productNameInput: oneProduct.productName,
            quantity: oneProduct.quantity,
            unitPriceInput: oneProduct.unitCost,
            quantityUI: oneProduct.quantity,
            unitPriceInputUI: oneProduct.unitCost,
            typeSelected: rmsInfo.selectedType,
            subTypeSelected: rmsInfo.selectedSubType,
            rmsItemNumber: rmsInfo.rmsItemNumber,
            productPricing: {
              totalCost: '',
              cashPrice: '',
              weeklyRate: '',
              weeklyTerm: '',
              priceWithFudgeFactor: '',
              rmsItemNumber: '',
              errMsg: '',
            },
          };

          resetProductList.push(one);
        });

        setProductListUI(resetProductList);
        setLocalProductsUI(resetProductList);
      }
    } else {
      resetProducts();
    }
  };

  const {
    setVendor,
    setWeeklyPayment,
    setWeeklyTerm,
    setTotalCost,
    setAllProducts,
    resetProducts,
  } = useShopLocalActions();

  useEffect(() => {
    if (errmsg) {
      setContinueEnabled(false);
    }
  }, [errmsg]);

  const onProcessPrefund = () => {
    const slaDecision = validatePrefundSLA(
      getNumericValue(totalCost) ?? 0,
      getNumericValue(storePrefundMaxAmount),
      getNumericValue(dmPrefundMaxAmount)
    );

    if (slaDecision.decision === 'REQUIRES_MANAGER_PIN' && !slaCompleted) {
      setShowSLA(true);
      return;
    }

    if (slaDecision.decision === 'EXCEEDS_DM_LIMIT') {
      setErrMsg(
        `This request exceeds the DM funding limit of $${dmPrefundMaxAmount}. Please remove items to reduce the total cost below this limit and try again.`
      );
      return;
    }

    if (slaDecision.decision === 'CONFIG_UNAVAILABLE') {
      setErrMsg(
        'Unable to validate funding limits at this time. Please try again later.'
      );
      return;
    }

    processProducts();
  };

  const processProducts = () => {
    const allProducts: ShopLocalProductInfo[] = [];
    productListUI.forEach((oneProduct) => {
      const one: ShopLocalProductInfo = {
        productName: oneProduct.productNameInput,
        quantity: oneProduct.quantity,
        unitCost: oneProduct.unitPriceInput,
        rmsInfo: {
          selectedDepartment: oneProduct.selectedDepartment.value,
          selectedSubDepartment: oneProduct.selectedSubDepartment.value,
          selectedBraket: oneProduct.selectedBraket.value,
          selectedType: oneProduct.typeSelected,
          selectedSubType: oneProduct.subTypeSelected,
          rmsItemNumber: oneProduct.rmsItemNumber,
        },
        weeklyRate: oneProduct.productPricing.weeklyRate,
        weeklyTerm: oneProduct.productPricing.weeklyTerm,
        totalCost: oneProduct.productPricing.totalCost,
        cashPrice: oneProduct.productPricing.cashPrice,
        errorMsg: '',
        leasable: true,
      };
      allProducts.push(one);
    });

    setAllProducts(allProducts);
    onProcess(true);
  };

  const resetSubAndBracket = (index: number) => {
    updateSubDepartmentSelected(emptyField, index);
    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        subdepartmentOptions: [emptyField],
      };

      return newList;
    });
    resetBracket(index);
  };

  const resetBracket = (index: number) => {
    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        selectedBraket: emptyField,
        bracketOptions: [emptyField],
      };

      return newList;
    });
  };

  useEffect(() => {
    if (estimatedWeeklyPayment && totalCost) {
      setContinueEnabled(true);
    } else {
      setContinueEnabled(false);
    }
  }, [estimatedWeeklyPayment, totalCost]);

  const SLACanceled = () => {
    setShowSLA(false);
  };

  // Handle SLA completion
  const handleSLACompleted = (completed?: boolean) => {
    setShowSLA(false);
    if (!completed) {
      return;
    }
    setSlaCompleted(true);
    processProducts();
  };

  const getNumericValue = (
    value: number | string | null | undefined
  ): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  useEffect(() => {
    const callAsyncPricing = async () => determinePricing();

    const processIt =
      vendorName &&
      lookUpPricing.current &&
      productListUI.every(
        (one) =>
          one.productNameInput &&
          one.subTypeSelected &&
          one.typeSelected &&
          one.selectedSubDepartment.value &&
          one.selectedDepartment.value &&
          one.selectedBraket.value &&
          one.productNameInput &&
          one.unitPriceInput &&
          one.quantity
      );

    if (processIt) {
      lookUpPricing.current = false;
      setErrMsg(undefined);
      setProgressToDeterminePricing(true);
      callAsyncPricing();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productListUI]);

  useEffect(() => {
    if (!progressToDeterminePricing) {
      lookUpPricing.current = true;
    }
  }, [progressToDeterminePricing]);

  const priceOneItem = async (aProduct: ShopLocalProductUI) => {
    const other = '';
    const payload = {
      bracket: aProduct.selectedBraket.value,
      costPerItem: aProduct.unitPriceInput,
      department: aProduct.selectedDepartment.value,
      itemSubType: aProduct.subTypeSelected,
      itemType: aProduct.typeSelected,
      poType: 'MANUAL',
      rmsItemNumber: other,
      storeNumber: window.sessionStorage.getItem('storeNumber'),
      subDepartment: aProduct.selectedSubDepartment.value,
    };
    const out = await getPricing(payload);

    if (out && out.data && out.data.result) {
      aProduct.productPricing.weeklyRate =
        out.data.result?.[0]?.newRate?.weeklyRate;
      aProduct.productPricing.totalCost =
        out.data.result?.[0]?.newRate?.totalCost;
      aProduct.productPricing.cashPrice =
        out.data.result?.[0]?.newRate?.cashPrice;
      aProduct.productPricing.weeklyTerm =
        out.data.result?.[0]?.newRate?.weeklyTerm;
      aProduct.rmsItemNumber = out.data.result?.[0]?.rmsItemNumber;
      aProduct.productPricing.rmsItemNumber =
        out.data.result?.[0]?.rmsItemNumber;
      const priceToFund = calcTotalPricesWithFudgeFactor(
        Number(aProduct.unitPriceInput)
      );
      aProduct.productPricing.priceWithFudgeFactor = priceToFund;
      aProduct.productPricing.errMsg = '';
    } else {
      const msg =
        'System Error ! No Pricing found for selected Bracket! Is another one possible?';
      aProduct.productPricing.errMsg = msg;
      setErrMsg(msg);
    }
  };

  const totalThePrice = () => {
    const totalPrice: number = productListUI.reduce(
      (total, one) => total + Number(one.quantity) * Number(one.unitPriceInput),
      0
    );
    const totalRate = productListUI.reduce(
      (total, one) => total + Number(one.productPricing.weeklyRate),
      0
    );
    const biggestTerm = Math.max(
      ...productListUI.map((p) => Number(p.productPricing.weeklyTerm))
    );

    const priceToFund = calcTotalPricesWithFudgeFactor(totalPrice);
    setTotalCost(priceToFund.toString());
    setWeeklyTerm(biggestTerm.toFixed(0));
    setWeeklyPayment(totalRate.toFixed(2));
  };
  const determinePricing = async () => {
    setContinueEnabled(false);
    const currentStatusAddProduct = addProductEnabled;
    setAddProductEnabled(false);
    setRemoveProductEnabled(false);

    for (const one of productListUI) {
      await priceOneItem(one);
    }

    totalThePrice();

    setProgressToDeterminePricing(false);
    setAddProductEnabled(currentStatusAddProduct);
    setRemoveProductEnabled(true);
  };

  const getDataById = (
    data: PricingData[],
    parentId: string
  ): any[] | undefined => {
    return (
      data &&
      data.filter(({ id }: { id: string }) => parentId.split('.')[0] === id)
    );
  };

  const drillDownToBrackets = (
    departmentId: string,
    subdepartmentId: string
  ): any => {
    const subList = getDataById(classifications, departmentId);
    if (subList) {
      const braketsList: any = getDataById(
        subList[0]?.subDepartments,
        subdepartmentId
      );
      return braketsList;
    }
  };

  const chooseBrackets = (departmentId: string, subdepartmentId: string) => {
    let chosenBraksList: ComboBoxData[] = [emptyField];

    const brakets = drillDownToBrackets(departmentId, subdepartmentId);
    if (brakets && brakets[0]?.brackets) {
      chosenBraksList = brakets[0].brackets.map((item: PricingData) => ({
        value: item.id,
        label: item.name,
      }));
    }

    return chosenBraksList;
  };

  const updateBracketsList = (
    departmentId: string,
    subdepartmentId: string,
    index: number
  ) => {
    const braksList = chooseBrackets(departmentId, subdepartmentId);

    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        bracketOptions: braksList,
      };

      return newList;
    });
    const brakets = drillDownToBrackets(departmentId, subdepartmentId);
    if (braksList.length == 1) {
      // only one so select it
      updateBracketSelected(braksList[0], index);
      const oneType: any[] | undefined = getDataById(
        brakets,
        braksList[0].value
      );
      updateTypeSelected(oneType?.[0]?.id, index);
      updateSubTypeSelected(oneType?.[0]?.subTypes?.[0]?.id, index);
    }
  };

  const chooseSubDepartments = (departmentId: string) => {
    const out: any = getDataById(classifications, departmentId);
    let chosenSubDepts: ComboBoxData[] = [emptyField];

    if (out && out[0]?.subDepartments) {
      chosenSubDepts = out[0]?.subDepartments.map((item: PricingData) => ({
        value: item.id,
        label: item.name,
      }));
    }

    return chosenSubDepts;
  };

  const updateSupDepartmentsList = (departmentId: string, index: number) => {
    const subDepts: ComboBoxData[] = chooseSubDepartments(departmentId);

    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        subdepartmentOptions: subDepts,
      };

      return newList;
    });

    if (subDepts.length == 1) {
      updateSubDepartmentSelected(subDepts[0], index);
      updateBracketsList(departmentId, subDepts[0].value, index);
    }
  };

  const setBracketComboBoxBasedOnSubDepartment = (
    subdepartmentId: string,
    index: number
  ) => {
    const selectedDeptId = productListUI[index].selectedDepartment.value;
    const bracketList = chooseBrackets(selectedDeptId, subdepartmentId);
    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        bracketOptions: bracketList,
      };

      return newList;
    });
  };

  const onDepartmentSelected = (selectedItem: ComboBoxData, index: number) => {
    resetSubAndBracket(index);
    updateSupDepartmentsList(selectedItem.value, index);
  };

  const onSubDepartmentSelected = (
    selectedItem: ComboBoxData,
    index: number
  ) => {
    resetBracket(index);
    setBracketComboBoxBasedOnSubDepartment(selectedItem.value, index);
  };

  useEffect(() => {
    const retrieveClassifications = async () => {
      let responseFromClassifications;

      if (enablePricingProductClassifications) {
        const storeNum = window.sessionStorage.getItem('storeNumber') || '';
        responseFromClassifications = await getPricingClassifications({
          storeNumber: [storeNum],
        });
        updateResponseToHaveStrings(
          responseFromClassifications?.data?.classificationsByDepts
        );
      } else {
        responseFromClassifications = await getClassifications();
      }

      removeAcimaItems(
        responseFromClassifications?.data?.classificationsByDepts
      );
      setClassifications(
        responseFromClassifications?.data?.classificationsByDepts
      );

      if (responseFromClassifications?.data?.classificationsByDepts) {
        const depts = responseFromClassifications.data.classificationsByDepts;
        const depList = depts.map((item: PricingData) => ({
          value: item.id,
          label: item.name,
        }));

        setDepartments(depList);

        setProductListUI((originalList) => {
          const newList = [...originalList];

          newList[0] = {
            ...newList[0],
            departmentOptions: depList,
          };

          return newList;
        });
      }
      setIsLoading(false);
    };

    retrieveClassifications();
    setTotalCost('');
    setWeeklyPayment('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const addProduct = () => {
    setContinueEnabled(false);
    if (productListUI.length > 2) setAddProductEnabled(false);

    if (productListUI.length < 4) {
      const oneMore = {
        ...emptyProductListUI,
        departmentOptions: departments,
      };
      const newList = [...productListUI];
      newList.push(oneMore);
      setProductListUI(newList);
      setLocalProductsUI(newList);
    }
  };

  const removeProduct = (indexToRemove: number) => {
    if (removeProductEnabled) {
      const listToUse = (prev) => prev.filter((_, i) => i !== indexToRemove);
      setProductListUI(listToUse);
      setLocalProductsUI(listToUse as unknown as ShopLocalProductUI[]);
      setAddProductEnabled(true);
    }
  };

  const updateDepartmentSelected = (newvalue: ComboBoxData, index) => {
    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        selectedDepartment: newvalue,
      };

      return newList;
    });
    onDepartmentSelected(newvalue, index);
  };
  const updateSubDepartmentSelected = (newvalue: ComboBoxData, index) => {
    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        selectedSubDepartment: newvalue,
      };

      return newList;
    });
    onSubDepartmentSelected(newvalue, index);
  };
  const updateBracketSelected = (newvalue: ComboBoxData, index) => {
    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        selectedBraket: newvalue,
      };

      return newList;
    });

    const brakets = drillDownToBrackets(
      productListUI[index].selectedDepartment.value,
      productListUI[index].selectedSubDepartment.value
    );
    if (brakets && brakets[0]?.brackets) {
      const oneBracket = brakets[0].brackets.find(
        (one) => (one.id = newvalue.value)
      );
      if (oneBracket) {
        updateTypeSelected(oneBracket.types?.[0]?.id, index);
        updateSubTypeSelected(oneBracket.types?.[0]?.subTypes?.[0]?.id, index);
      } else {
        // todo:  BAD DATA NO Types No SubTypes at all passed !!!!
        setErrMsg('System Error ! No Brackets were found !');
      }
    }
  };

  const updateProductName = (newvalue: string, index) => {
    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        productNameInput: newvalue,
      };

      return newList;
    });
  };

  const updateTypeSelected = (newvalue: string, index) => {
    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        typeSelected: newvalue,
      };

      return newList;
    });
  };

  const updateSubTypeSelected = (newvalue: string, index) => {
    setProductListUI((originalList) => {
      const newList = [...originalList];

      newList[index] = {
        ...newList[index],
        subTypeSelected: newvalue,
      };

      return newList;
    });
  };

  const updateQuantity = (newvalue: string, index) => {
    const newList = [...productListUI];

    newList[index] = {
      ...newList[index],
      quantity: newvalue,
      quantityUI: newvalue,
    };

    setProductListUI(newList);
    setLocalProductsUI(newList as unknown as ShopLocalProductUI[]);
  };

  const updateQuantityUI = (newvalue: string, index) => {
    const newList = [...productListUI];

    newList[index] = {
      ...newList[index],
      quantityUI: newvalue,
    };

    setLocalProductsUI(newList as unknown as ShopLocalProductUI[]);
  };

  const updateUnitPriceInput = (newvalue: string, index) => {
    const newList = [...productListUI];

    newList[index] = {
      ...newList[index],
      unitPriceInput: newvalue,
      unitPriceInputUI: newvalue,
    };

    setProductListUI(newList);
    setLocalProductsUI(newList as unknown as ShopLocalProductUI[]);
  };

  const updateUnitPriceInputUI = (newvalue: string, index) => {
    const newList = [...productListUI];

    newList[index] = {
      ...newList[index],
      unitPriceInputUI: newvalue,
    };

    setLocalProductsUI(newList as unknown as ShopLocalProductUI[]);
  };

  const displayOneProductUI = (one: ShopLocalProductUI, index: number) => {
    return (
      <>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          classes={{ root: classes.grid }}
        >
          <div className={`${classes.productdetailsgrid}`}>
            <RACTextbox
              type="text"
              inputlabel={t('Product Name')}
              name={`productNameInput${index}`}
              id={`productNameInput${index}`}
              required={true}
              disabled={false}
              value={one.productNameInput}
              className={`${classes.labelHeader}`}
              inputLabelClassname={`${classes.labelHeader}`}
              OnChange={(e) => updateProductName(e.target.value, index)}
            />
          </div>
          <div className={`${classes.productdetailSmallgrid}`}>
            <RACTextbox
              type="text"
              inputlabel={t('Quantity')}
              name={`quantity${index}`}
              id={`quantity${index}`}
              required={true}
              disabled={false}
              value={localProductsUI[index].quantityUI}
              className={`${classes.productdetailMidgrid}`}
              inputLabelClassname={`${classes.productspecial}`}
              OnChange={(e) => updateQuantityUI(e.target.value, index)}
              Onblur={(e) => updateQuantity(e.target.value, index)}
            />
          </div>
          <div className={`${classes.pdsg}`}>
            <RACTextbox
              type="text"
              inputlabel={t('Unit Price')}
              name={`unitPriceInput${index}`}
              id={`unitPriceInput${index}`}
              isCurrency={true}
              required={true}
              disabled={false}
              digitFormat={'currency'}
              value={localProductsUI[index].unitPriceInputUI}
              className={`${classes.productdetailMidgrid}`}
              inputLabelClassname={`${classes.productspecial}`}
              OnChange={(e) => updateUnitPriceInputUI(e.target.value, index)}
              Onblur={(e) => updateUnitPriceInput(e.target.value, index)}
            />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          classes={{ root: classes.grid }}
        >
          <RACSearchDropdown
            name="depart"
            inputLabel="Department"
            options={one.departmentOptions}
            OnChange={(e) => updateDepartmentSelected(e, index)}
            // OnChange={onDepartmentSelected}
            required
            className={`${classes.comboStyle}`}
            disabled={false}
            value={one.selectedDepartment}
          />
          <RACSearchDropdown
            name="subdepart"
            inputLabel="SubDepartment"
            options={one.subdepartmentOptions}
            OnChange={(e) => updateSubDepartmentSelected(e, index)}
            required
            className={`${classes.comboStyle}`}
            disabled={false}
            value={one.selectedSubDepartment}
          />
          <RACSearchDropdown
            name="brack"
            inputLabel="Bracket"
            options={one.bracketOptions}
            OnChange={(e) => updateBracketSelected(e, index)}
            required
            className={`${classes.comboStyle}`}
            disabled={false}
            value={one.selectedBraket}
          />
          {index > 0 && (
            <div className={`${classes.removeDivStyle}`}>
              <Typography
                variant="subtitle2"
                className={
                  removeProductEnabled
                    ? classes.removeProductLink
                    : classes.addLinkDisabled
                }
                data-testid="SetToDefault"
                style={{ cursor: 'pointer' }}
                onClick={
                  removeProductEnabled ? () => removeProduct(index) : null
                }
              >
                {t(`-Remove Product ${index + 1}`)}
              </Typography>
            </div>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          classes={{ root: classes.spacingItem }}
        >
          <Divider
            style={{ height: 1, backgroundColor: '#777777', opacity: '.25' }}
            orientation="horizontal"
          ></Divider>
        </Grid>
      </>
    );
  };

  useEffect(() => {
    if (!isLoading) {
      // when it is changed to false then check to see if in edit mode
      updateDisplayToPreviouslyUsed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <RACModal
      isOpen
      classes={{
        dialogContent: classes.dialogContent,
        dialogActions: classes.dialogActions,
        dialog: classes.dialogRoot,
        dialogTitle: classes.dialogTitle,
      }}
      maxWidth="lg"
      title={t('Prefund Request Confirmation')}
      titleVariant="h4"
      content={
        isLoading ? (
          <>
            <Card>
              <CardContent className={clsx(classes.loadingprogres)}>
                <CircularProgress />
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <div className={clsx(classes.shopLocalsRoot)}>
              {showSLA && (
                <SecondFactor
                  setTwoFactorCancelClick={() => SLACanceled()}
                  setTwoFactorCompleted={handleSLACompleted}
                  moduleName={'CustomerRetention'}
                  currentRole={
                    window.localStorage.getItem('currentRole') || DEFAULT_ROLE
                  }
                />
              )}
              <Grid container spacing={0}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  classes={{ root: classes.grid }}
                >
                  <Typography className={clsx(classes.shopLocalHeader)}>
                    {'Add the vendor, product details and other required info'}{' '}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <RACTextbox
                    type="text"
                    inputlabel={t('Vendor Name')}
                    name="vendorNameInput"
                    id="vendorNameInput"
                    required={true}
                    maxlength={290}
                    disabled={false}
                    value={vendorName}
                    className={`${classes.fullItem}`}
                    OnChange={(e) => {
                      setVendor(e.target.value);
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  classes={{ root: classes.grid }}
                >
                  <Typography className={clsx(classes.labelGroup)}>
                    {'Product Details'}
                  </Typography>

                  <div className={`${classes.addProductLink}`}>
                    <Typography
                      variant="subtitle2"
                      className={
                        addProductEnabled
                          ? classes.addProductLink
                          : classes.addLinkDisabled
                      }
                      data-testid="SetToDefault"
                      style={{ cursor: 'pointer' }}
                      onClick={addProductEnabled ? () => addProduct() : null}
                    >
                      {t('+Add Product')}
                    </Typography>
                  </div>
                </Grid>
                {productListUI.map((one, index) => (
                  <React.Fragment key={index}>
                    {displayOneProductUI(one, index)}
                  </React.Fragment>
                ))}
              </Grid>

              <RACCard className={clsx(classes.card)}>
                <div className={clsx(classes.cardBody)}>
                  <Grid container classes={{ root: classes.answergrid }}>
                    <Grid
                      item
                      xs={5}
                      sm={5}
                      md={5}
                      lg={5}
                      classes={{ root: classes.answergrid }}
                    >
                      <Typography className={clsx(classes.label)}>
                        {'Total Financing Request'}{' '}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={2}
                      sm={2}
                      md={2}
                      lg={2}
                      classes={{ root: classes.divideStyle }}
                    >
                      <Divider
                        style={{ width: 4 }}
                        orientation="vertical"
                      ></Divider>
                    </Grid>
                    <Grid
                      item
                      xs={5}
                      sm={5}
                      md={5}
                      lg={5}
                      classes={{ root: classes.answergrid }}
                    >
                      <Typography className={clsx(classes.label)}>
                        {'Estimated Weekly Payment'}{' '}
                      </Typography>
                    </Grid>

                    <Grid
                      item
                      xs={5}
                      sm={5}
                      md={5}
                      lg={5}
                      classes={{ root: classes.answergrid }}
                    >
                      <Typography className={clsx(classes.label)}>
                        {totalCost}{' '}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={2}
                      sm={2}
                      md={2}
                      lg={2}
                      classes={{ root: classes.divideStyle }}
                    >
                      <Divider
                        style={{ width: 4 }}
                        orientation="vertical"
                      ></Divider>
                    </Grid>
                    <Grid
                      item
                      xs={5}
                      sm={5}
                      md={5}
                      lg={5}
                      classes={{ root: classes.answergrid }}
                    >
                      <Typography className={clsx(classes.label)}>
                        {estimatedWeeklyPayment}{' '}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={5}
                      sm={5}
                      md={5}
                      lg={5}
                      classes={{ root: classes.answergrid }}
                    >
                      <Typography className={clsx(classes.label)}>
                        {'( +tax )'}{' '}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={2}
                      sm={2}
                      md={2}
                      lg={2}
                      classes={{ root: classes.divideStyle }}
                    >
                      <Divider
                        style={{ width: 4 }}
                        orientation="vertical"
                      ></Divider>
                    </Grid>
                    <Grid
                      item
                      xs={5}
                      sm={5}
                      md={5}
                      lg={5}
                      classes={{ root: classes.answergrid }}
                    >
                      <Typography className={clsx(classes.label)}>
                        {'( +tax )'}{' '}
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
              </RACCard>
            </div>
          </>
        )
      }
      onClose={() => onClose(false)}
      buttons={
        <>
          <Grid container>
            <Grid item xs={8} sm={8} md={8} lg={8}>
              {progressToDeterminePricing && (
                <>
                  <Typography className={clsx(classes.label)}>
                    {'Determining the pricing for the Product(s) entered ... '}
                  </Typography>
                  <CircularProgress />
                </>
              )}
              {errmsg && (
                <Typography className={clsx(classes.errorstyle)}>
                  {errmsg}
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={4}
              sm={4}
              md={4}
              lg={4}
              classes={{ root: classes.buttongrid }}
            >
              <RACButton
                // eslint-disable-next-line
                // @ts-ignore
                variant="outlined"
                color="secondary"
                onClick={() => onClose(false)}
                disabled={false}
              >
                {t('Cancel')}
              </RACButton>
              <RACButton
                // eslint-disable-next-line
                // @ts-ignore
                variant="contained"
                color="primary"
                onClick={() => onProcessPrefund()}
                disabled={!continueEnabled}
              >
                {t('Confirm Request')}
              </RACButton>
            </Grid>
          </Grid>
        </>
      }
    />
  );
};
